import React, { useState } from 'react';
import { Provider } from 'react-redux';
import { ArgoContext,useArgoContext } from '@amzn/awsscm-argo-core';
import { ApolloProvider } from '@apollo/client';
import { ErrorResponse,onError } from '@apollo/client/link/error';
import cloneDeep from 'lodash/cloneDeep';
import { isExternalArgoUser } from 'rfxportal/RFX/userPermissionHelper';

import { getConfigValue } from './config/appConfig';
import { ERROR_MESSAGES } from './constants/ErrorMessages';
import { ErrorName } from './data/UIConstants';
import { ApolloClientWrapper } from './gql';
import ErrorBoundary from './rfxportal/components/common/ErrorBoundary';
import RoutingTable from './rfxportal/RFX/RoutingTable';
import store from './store';

import './App.css';

export default function App() {
  const [graphQLError, setGraphQLError] = useState('');
  const DEFAULT_ERROR_MESSAGE = '(Unexpected Internal Server Error): Retrying your request might resolve the issue.';

  const shouldIgnoreComingGraphQLError = (currentGraphQLError: string, comingGraphQLError: string): boolean => {
    // exclude attachment not found issue since it needs time to propagate in RFX backend
    if (comingGraphQLError.includes('not found in RFXSvcAttachmentTable')) return true;
    // exclude attachment not uploaded issue since it needs time to propagate in RFX backend
    if (comingGraphQLError.includes("hasn't been uploaded to S3 bucket yet")) return true;
    // exclude duplicate bulkProcessLineItems calls error for old requests that are missing bulk processing status
    if (comingGraphQLError.includes('each versioned external id can only be processed once')) return true;
    // exclude no IPN in quote issue as it will be handled by user confirmation when publishing
    if (comingGraphQLError.includes('No IPNs found for the RFXRequest')) return true;
    // exclude no Commodity IPN in quote issue as it will be handled by user confirmation when publishing
    if (comingGraphQLError.includes('No Commodity IPNs found for the RFXRequest')) return true;
    // exclude read/write racing condition
    if (comingGraphQLError.includes('Bom validation process is already in-progress')) return true;
    // exclude Exploded BOM not found issue since this is not an error
    if (comingGraphQLError.includes('BOM Specification for partNumber')) return true;
    // exclude getBulkProcessStatus error since it's still processing
    if (comingGraphQLError.includes('Bulk process status not found for')) return true;
    // exclude getBulkProcessParsedContent error since it's not found
    if (comingGraphQLError.includes('Bulk process parsed content not found for')) return true;
    // exclude company code not found issue for dev cells due to data availability
    if (
      (comingGraphQLError.includes('unique company code') || comingGraphQLError.includes('Invalid site')) &&
      getConfigValue('ignoreEquipmentServiceError')
    )
      return true;
    // exclude user creation errors handled by invite contact component
    if (comingGraphQLError.includes(ERROR_MESSAGES.USER_ALREADY_EXISTS)) {
      return true;
    }
    if (comingGraphQLError.includes(ERROR_MESSAGES.USER_DISABLED)) {
      return true;
    }
    return currentGraphQLError === comingGraphQLError;
  };

  const sanitizeErrorMessage = (comingGraphQLError: string): string => {
    if (!comingGraphQLError || comingGraphQLError === 'null') {
      return DEFAULT_ERROR_MESSAGE;
    } else {
      return comingGraphQLError;
    }
  };

  const graphQLErrorLink = onError(({ graphQLErrors }: ErrorResponse) => {
    if (graphQLErrors?.length) {
      let comingGraphQLError: string = cloneDeep(graphQLErrors[0].message);

      if (!shouldIgnoreComingGraphQLError(graphQLError, comingGraphQLError)) {
        setGraphQLError(sanitizeErrorMessage(comingGraphQLError));
      }
    }
  });
  const context: ArgoContext = useArgoContext();
  const { initialMetricsPublisher, logger } = context;
  const apolloClient = new ApolloClientWrapper(context, graphQLErrorLink);
  const isExternalUser: boolean = isExternalArgoUser();
  const publisher = initialMetricsPublisher.newChildActionPublisherForMethod(ErrorName.unhandledRejection);

  window.addEventListener('unhandledrejection', function (event) {
    console.error(`Unhandled rejection: ${event.reason}`);
    logger.error(event.reason, { error: ErrorName.unhandledRejection });
    publisher.publishCounterMonitor(ErrorName.unhandledRejection, 1);
  });

  React.useEffect(() => {
    context.rumClient?.recordEvent('ApplicationLoadSuccess', {
      appName: 'smart',
      timestamp: new Date().toISOString(),
      initialRoute: window.location.pathname,
      loadTime: performance.now(),
    });
  }, [context.rumClient]);

  return (
    // Adding an Apollo provider high in the component tree makes it available for all children to access.
    // See https://www.apollographql.com/docs/react/get-started/ for more info.
    <ErrorBoundary apolloError={graphQLError} setGraphQLError={setGraphQLError} isExternalUser={isExternalUser}>
      <ApolloProvider client={apolloClient.client}>
        <Provider store={store}>
          <RoutingTable />
        </Provider>
      </ApolloProvider>
    </ErrorBoundary>
  );
}
